import { useState } from "react";
import {
  Grid,
  Paper,
  Avatar,
  Button,
  Typography,
  Stack,
  CircularProgress,
  Box,
  Container,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useNavigate } from "react-router-dom";
import {
  setUserEmail,
  setUserName,
  setSignInTime,
  setIsAdmin,
} from "../utils/sessionCheck";
import { auth, provider, firestore } from "../firebase.config";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { signInWithPopup } from "firebase/auth";
import { useUserContext } from "../context/UserContext";
import ReCAPTCHA from "react-google-recaptcha";
import { generateJWT } from "../context/Api";
import logo from "../logo512.png";
import { styled } from "@mui/material/styles";
import GoogleIcon from "@mui/icons-material/Google";
import { SvgIcon } from "@mui/material";
import "../index.css"; // ← This applies globally

// Custom 4-color Google Icon component
const CustomGoogleIcon = () => (
  <SvgIcon
    sx={{
      width: 24,
      height: 24,
    }}
  >
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      {/* Red section (top) */}
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#EA4335"
      />
      {/* Blue section (right) */}
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#4285F4"
      />
      {/* Green section (bottom) */}
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#34A853"
      />
      {/* Yellow section (left) */}
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#FBBC05"
      />
    </svg>
  </SvgIcon>
);

const StyledText = styled(Typography)(
  ({ fontfamily, fontweight, fontsize, fontcolor }) => ({
    fontFamily: fontfamily || "Roboto, sans-serif",
    fontWeight: fontweight || 400,
    fontSize: fontsize || "1rem",
    color: fontcolor || "#fff",
    margin: 0,
  })
);

// Common styles for floating cards
const COMMON_CARD_STYLES = {
  width: 120,
  height: 120,
  p: 2,
  borderRadius: "24px",
  background: "rgba(255,255,255,0.08)",
  border: "1px solid rgba(255,255,255,0.6)",
  backdropFilter: "blur(8px)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
};

// Common text styles for card labels
const COMMON_LABEL_STYLES = {
  color: "#ffffffff",
  fontWeight: 500,
  fontSize: "1rem",
};

// Common text styles for card values
const COMMON_VALUE_STYLES = {
  color: "#ffffffff",
  fontWeight: 700,
  fontSize: "1.2rem",
};

// Card configurations with their specific properties
const FLOATING_CARDS = [
  {
    id: "streak",
    position: { top: 60, left: 40 },
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
    label: "Streak",
    value: "5 Days",
    labelStyles: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 200,
      fontSize: "1.2rem",
      lineHeight: 1.2,
    },
    valueStyles: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 700,
      fontSize: "1.2rem",
      lineHeight: 1.2,
    }
  },
  {
    id: "tasksDone",
    position: { top: 500, left: 90 },
    boxShadow: "0 8px 32px rgba(124,58,237,0.15)",
    label: "Tasks Done",
    value: "12/15"
  },
  {
    id: "productivity",
    position: { bottom: 80, right: 60 },
    boxShadow: "0 8px 32px rgba(59,130,246,0.15)",
    label: "Productivity",
    value: "85%",
    valueStyles: { fontSize: "1.4rem" }
  },
  {
    id: "focusTime",
    position: { top: 40, right: 200 },
    boxShadow: "0 8px 32px rgba(231,58,237,0.13)",
    label: "Focus Time",
    value: "2h 45m"
  },
  {
    id: "energy",
    position: { top: 300, right: 120 },
    boxShadow: "0 8px 32px rgba(231,58,237,0.13)",
    label: "Energy",
    value: "High",
    valueStyles: { fontSize: "1.4rem" }
  }
];


const SignIn = () => {
  const navigate = useNavigate();
  const { setUserId, setUserRole, setTenantId, setSelectedTenantName } = useUserContext();
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));


  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  const handleRecaptchaChange = (value) => {
    if (value) {
      setIsVerified(true);
    }
  };

  const logGoogleUser = async () => {
    if (!isVerified) {
      alert("Please complete the reCAPTCHA");
      return;
    }
  
    setLoading(true);
  
    try {
    //   // Step 1: Sign in with Google
    //   const data = await signInWithPopup(auth, provider);
    //   const email = data.user.email.toLowerCase();
    //   const displayName = data.user.displayName;
    //   const photoURL = data.user.photoURL;
  
    //   // Step 2: Set essential session data immediately
    //   setUserEmail(email);
    //   setUserName(displayName);
    //   setSignInTime(new Date().toUTCString());
    
  
    //   // Step 3: Run parallel operations
    //   const [userQuery, jwtResponse] = await Promise.all([
    //     // Check if user exists and is active
    //     getDocs(
    //       query(
    //         collection(firestore, "Users"),
    //         where("userEmail", "==", email),
    //         where("Is_Active", "==", true)
    //       )
    //     ),
    //     // Generate JWT in parallel
    //     generateJWT(email, "tenant-123", null) // We'll update the role later if needed
    //   ]);
  
    //   if (!userQuery.empty) {
    //     const userDoc = userQuery.docs[0];
    //     const role = userDoc.data().role;
    //     const userId = userDoc.id;
  
    //     // Store user ID
    //     setUserId(userId);
  
    //     // Update JWT with correct role if needed
    //     let token = jwtResponse.token;
    //     if (role !== null) {
    //       const updatedJwtResponse = await generateJWT(email, "tenant-123", role);
    //       token = updatedJwtResponse.token;
    //     }
  
    //     // Store session data
    //     sessionStorage.setItem(
    //       "userData",
    //       JSON.stringify({
    //         email,
    //         displayName,
    //         photoURL,
    //         jwtToken: token,
    //         role,
    //       })
    //     );
  
    //     // Set admin status and navigate
    //     const isAdmin = role === "admin";
    //     setIsAdmin(email); // This is now non-blocking
    //     navigate("/timer", { state: { userId } });
  
    //     // Defer tenant check/creation to after sign-in
    //     const tenantId = "tenant-123";
    //     getDoc(doc(firestore, "TenantMaster", tenantId)).then(async (tenantSnap) => {
    //       if (!tenantSnap.exists()) {
    //         await setDoc(doc(firestore, "TenantMaster", tenantId), {
    //           tenantId,
    //           tenantName: "Default Tenant",
    //           createdAt: new Date().toISOString(),
    //         });
    //       }
    //     }).catch(console.error); // Handle error silently to not affect user experience
    //   } else {
    //     console.log("User not found or inactive. Access denied.");
    //     alert("Access denied: You are not an active user.");
    //     navigate("/");
    //   }
    // } catch (error) {
    //   if (error.code === "auth/popup-closed-by-user") {
    //     console.warn("Popup closed by user, please try again.");
    //     alert(
    //       "The sign-in popup was closed before completing the process. Please try again."
    //     );
    //   } else {
    //     console.error("Error during sign-in or querying Firestore:", error);
    //     alert("An error occurred during sign-in. Please try again.");
    //   }
    //   navigate("/");
    
    const data = await signInWithPopup(auth, provider);
    const email = data.user.email.toLowerCase();
    const displayName = data.user.displayName;
    const photoURL = data.user.photoURL;

    setUserEmail(email);
    setUserName(displayName);
    setSignInTime(new Date().toUTCString());

    // Check if email matches superadmin
    const superAdmins = [
      process.env.REACT_APP_SUPERADMIN_EMAIL1,
      process.env.REACT_APP_SUPERADMIN_EMAIL2,
      process.env.REACT_APP_SUPERADMIN_EMAIL3,
      process.env.REACT_APP_SUPERADMIN_EMAIL4,
      
    ];
    if (superAdmins.includes(email)) {
      setUserRole("SuperAdmin");
      sessionStorage.setItem("userRole", "SuperAdmin");
      setTenantId(null);
      setSelectedTenantName(null);

      sessionStorage.setItem("userData", JSON.stringify({
        email, displayName, photoURL, jwtToken: "", role: "SuperAdmin", tenantId: null
      }));
      return navigate("/select-tenant");
    }

    // If not SuperAdmin, fetch user from Firestore
    const userQuery = await getDocs(
      query(
        collection(firestore, "Users"),
        where("userEmail", "==", email),
        where("Is_Active", "==", true)
      )
    );
    if (userQuery.empty) {
      alert("Access denied: You are not an active user.");
      setLoading(false);
      return navigate("/");
    }

    const userDoc = userQuery.docs[0];
    const userData = userDoc.data();
    if (!userData.Is_Active) {
      alert("Your account has been deactivated.");
      setLoading(false);
      return navigate("/");
    }

    const userId = userDoc.id;
    const role = userData.role || "user";
    const tenantId = userData.tenantId || "";
    const tenantName = userData.tenantName || "";

    //Checks if user is associated with tenant
    if (!tenantId) {
      alert("Access denied: You are not associated with any tenant.");
      return navigate("/");
    }

    setUserId(userDoc.id);
    setUserRole(role);
    setTenantId(tenantId);
    setSelectedTenantName(tenantName);

    sessionStorage.setItem("selectedTenantId", tenantId);
    sessionStorage.setItem("selectedTenantName", tenantName);
    sessionStorage.setItem("userRole", role); // Save role to session storage
    
    const jwtResponse = await generateJWT(email, tenantId, role);
    sessionStorage.setItem("userData", JSON.stringify({
      email,
      displayName,
      photoURL,
      jwtToken: jwtResponse.token,
      role,
      tenantId,
      tenantName
    }));

    setIsAdmin(email);
    navigate("/dashboard");

    } finally {
      setLoading(false);
    }
  };
  

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #3b82f6 0%, #7c3aed 50%, rgb(231, 58, 237) 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        px: isMobile ? 2 : 0,
        py: isMobile ? 2 : 0,
        pb: isMobile ? 8 : 10,
      }}
    >
      {/* Decorative Elements */}
      {/* <Box
        sx={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: "rgba(193, 54, 54, 0.05)",
        }}
      /> */}
      {/* <Box
        sx={{
          position: "absolute",
          bottom: -50,
          left: -50,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background: "rgba(249, 20, 20, 0.05)",
        }}
      /> */}
      {/* Floating Message Cards - Responsive positioning */}
      {FLOATING_CARDS.map((card) => (
        <Box
          key={card.id}
          sx={{
            position: "absolute",
            ...(isMobile ? {
              // Mobile positioning - hide most cards on very small screens
              display: card.id === 'streak' || card.id === 'productivity' ? 'flex' : 'none',
              ...COMMON_CARD_STYLES,
              width: 80,
              height: 80,
              p: 1.5,
              ...(card.id === 'streak' ? { top: 20, left: 20 } : {}),
              ...(card.id === 'productivity' ? { bottom: 20, right: 20 } : {}),
            } : isTablet ? {
              // Tablet positioning
              ...COMMON_CARD_STYLES,
              width: 100,
              height: 100,
              p: 1.5,
              ...card.position,
            } : {
              // Desktop positioning
              ...COMMON_CARD_STYLES,
              ...card.position,
            }),
            boxShadow: card.boxShadow,
            zIndex: 0,
          }}
        >
          <Typography
            sx={{
              ...COMMON_LABEL_STYLES,
              ...(card.labelStyles || {}),
              fontSize: isMobile ? '0.8rem' : isTablet ? '0.9rem' : '1rem',
            }}
          >
            {card.label}
          </Typography>

          <Typography
            sx={{
              ...COMMON_VALUE_STYLES,
              ...(card.valueStyles || {}),
              fontSize: isMobile ? '1rem' : isTablet ? '1.1rem' : '1.2rem',
            }}
          >
            {card.value}
          </Typography>
        </Box>
      ))}

      {/* Logo Container - Responsive sizing */}
      <Box
        sx={{
          mb: isMobile ? 2 : 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: isMobile ? 2 : 3,
        }}
      >
        <Box
          sx={{
            p: isMobile ? 1.5 : 2,
            borderRadius: "50%",
            backgroundColor: "#1E90FF",
            border: isMobile ? "2px solid white" : "3px solid white",
            backdropFilter: "blur(10px)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={logo}
            alt="Logo"
            style={{
              width: isMobile ? "40px" : "60px",
              height: isMobile ? "40px" : "60px",
              objectFit: "contain",
              filter: "drop-shadow(0 4px 8px rgba(150, 54, 54, 0.2))",
            }}
          />
        </Box>

        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{
              color: "#fff",
              fontWeight: 600,
              textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
              mb: isMobile ? 0.5 : 1,
              fontSize: isMobile ? "1.5rem" : "2.125rem",
            }}
          >
            Timer
          </Typography>
          <Typography
            variant={isMobile ? "body1" : "h6"}
            sx={{
              color: "rgba(255, 255, 255, 0.85)",
              fontWeight: 400,
              textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
              fontSize: isMobile ? "0.875rem" : "1.25rem",
              px: isMobile ? 1 : 0,
            }}
          >
            Track your time, boost your productivity
          </Typography>
        </Box>
      </Box>

      {/* Sign-in Card - Responsive sizing and spacing */}
      <Paper
        elevation={24}
        sx={{
          p: isMobile ? 3 : 4,
          borderRadius: 4,
          width: "100%",
          maxWidth: isMobile ? 320 : 380,
          background: "#ffffff",
          boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
          mx: isMobile ? 2 : 0,
          zIndex: 2,
          mb: isMobile ? 3 : 4,
        }}
      >
        <Stack spacing={isMobile ? 2.5 : 3} alignItems="center">
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{
              fontWeight: 700,
              fontSize: isMobile ? "1.25rem" : "1.5rem",
              color: "#000000ff",
              textAlign: "center",
            }}
          >
            Welcome Back
          </Typography>

          <Typography 
            variant={isMobile ? "body1" : "h5"} 
            sx={{ 
              fontWeight: 400, 
              fontSize: isMobile ? "0.875rem" : "1.0rem",
              textAlign: "center",
            }}
          >
            Sign in to continue
          </Typography>

          <Box sx={{ 
            transform: isMobile ? 'scale(0.9)' : 'scale(1)',
            transformOrigin: 'center',
            width: '100%',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <ReCAPTCHA
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={handleRecaptchaChange}
            />
          </Box>

          <Button
            onClick={logGoogleUser}
            disabled={loading || !isVerified}
            variant="contained"
            fullWidth
            sx={{
              mt: isMobile ? 1.5 : 2,
              py: isMobile ? 1.25 : 1.5,
              background: "#ffffff",
              color: "#111827",
              '&:hover': { background: "#f5f5f5" },
              '&:disabled': { background: "#e5e7eb", color: "#9ca3af" },
              boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              textTransform: "none",
              fontWeight: 600,
              fontSize: isMobile ? "0.95rem" : "1rem",
            }}
            startIcon={
              loading ? (
                <CircularProgress size={isMobile ? 18 : 20} sx={{ color: 'black' }} />
              ) : (
                <svg width={isMobile ? 18 : 20} height={isMobile ? 18 : 20} viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )
            }
          >
            {loading ? 'Signing In...' : 'Sign In with Google'}
          </Button>
        </Stack>
      </Paper>

      {/* Footer - Responsive positioning */}
      <Typography
        variant="body2"
        sx={{
          color: "rgba(255,255,255,0.6)",
          position: "absolute",
          bottom: isMobile ? 16 : 24,
          textAlign: "center",
          fontSize: isMobile ? "0.75rem" : "0.875rem",
          px: isMobile ? 2 : 0,
        }}
      >
        © {new Date().getFullYear()} OV Time. All rights reserved.
      </Typography>
    </Box>
  );
};

export default SignIn;